import { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchRealProjects } from '../services/api'
import { milestones, products, getMilestoneStats, daysRemaining } from '../data/milestones'
import NCAEccPlan from './NCAEccPlan'

// ═══ Animated Number ═══
function AnimatedNum({ value }) {
  return <span>{typeof value === 'number' ? value.toLocaleString('ar-SA') : value || '—'}</span>
}

// ═══ Progress Bar ═══
function Bar({ progress, color, height = 'h-2' }) {
  const p = Math.max(0, Math.min(100, progress || 0))
  return (
    <div className={`w-full ${height} bg-white/[0.06] rounded-full overflow-hidden`}>
      <div className="h-full rounded-full transition-all duration-1000 ease-out"
        style={{ width: `${p}%`, background: color, boxShadow: `0 0 10px ${color}40` }} />
    </div>
  )
}

// ═══ Status Badge ═══
function Badge({ status }) {
  const styles = {
    healthy:     { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'يعمل' },
    built:       { bg: 'bg-blue-500/15',    text: 'text-blue-400',    label: 'جاهز' },
    done:        { bg: 'bg-emerald-500/15',  text: 'text-emerald-400', label: 'مكتمل' },
    in_progress: { bg: 'bg-amber-500/15',   text: 'text-amber-400',   label: 'قيد التنفيذ' },
    pending:     { bg: 'bg-white/[0.04]',    text: 'text-[#94A3B8]',   label: 'معلق' },
    overdue:     { bg: 'bg-red-500/15',      text: 'text-red-400',     label: 'متأخر' },
    blocked:     { bg: 'bg-red-500/15',      text: 'text-red-400',     label: 'محجوب' },
    active:      { bg: 'bg-blue-500/15',     text: 'text-blue-400',    label: 'نشط' },
    down:        { bg: 'bg-red-500/15',      text: 'text-red-400',     label: 'متوقف' },
    planned:     { bg: 'bg-white/[0.04]',    text: 'text-[#475569]',   label: 'مخطط' },
    unknown:     { bg: 'bg-white/[0.04]',    text: 'text-[#475569]',   label: '—' },
  }
  const s = styles[status] || styles.unknown
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  )
}

// ═══ Token Chip ═══
function TokenChip({ tokens }) {
  if (!tokens) return null
  const formatted = tokens >= 1000 ? `${(tokens / 1000).toFixed(0)}K` : tokens.toString()
  return (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 whitespace-nowrap">
      🪙 {formatted}
    </span>
  )
}

// ═══════════════════════════════════
//  TASK ROW (inside milestone drill-down)
// ═══════════════════════════════════
function TaskRow({ task, tokenPerTask, index }) {
  const days = daysRemaining(task.dueDate)
  const isOverdue = days !== null && days < 0 && task.status !== 'done'
  const status = isOverdue ? 'overdue' : task.status

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-200
      ${isOverdue ? 'bg-red-500/[0.04] border border-red-500/10' : 'bg-white/[0.02] hover:bg-white/[0.04]'}`}
      style={{ animation: `fadeInUp 0.4s ease-out ${index * 50}ms both` }}>

      {/* Progress dot */}
      <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold"
        style={{
          background: task.status === 'done' ? '#22C55E20' : isOverdue ? '#EF444420' : '#8B5CF610',
          color: task.status === 'done' ? '#22C55E' : isOverdue ? '#EF4444' : '#94A3B8',
        }}>
        {task.status === 'done' ? '✓' : `${task.progress}%`}
      </div>

      {/* Task info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[13px] font-medium text-white truncate">{task.name}</p>
          <Badge status={status} />
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <span className="text-[10px] text-[#64748B]">👤 {task.assignee}</span>
          <span className="text-[10px] text-[#64748B]">📅 {task.dueDate}</span>
          {isOverdue && <span className="text-[10px] text-red-400 font-bold">⚠️ تأخر {Math.abs(days)} يوم</span>}
        </div>
        {task.notes && (
          <p className="text-[10px] text-[#475569] mt-1 truncate">{task.notes}</p>
        )}
      </div>

      {/* Tokens + progress */}
      <div className="flex-shrink-0 text-left space-y-1.5 w-24">
        <TokenChip tokens={tokenPerTask} />
        <Bar progress={task.progress} color={task.progress === 100 ? '#22C55E' : isOverdue ? '#EF4444' : '#8B5CF6'} height="h-1.5" />
      </div>
    </div>
  )
}

// ═══════════════════════════════════
//  MILESTONE CARD (expandable)
// ═══════════════════════════════════
function MilestoneCard({ milestone, realData, index, isOpen, onToggle }) {
  const stats = getMilestoneStats(milestone)
  const prodConfig = products[milestone.product] || {}
  const color = prodConfig.primary || '#8B5CF6'

  // Calculate token distribution
  const allProjectMilestones = milestones.filter(m => m.product === milestone.product)
  const totalTasksInProject = allProjectMilestones.reduce((s, m) => s + m.tasks.length, 0)
  const projectTokens = realData?.estimatedTokens || 0
  const milestoneTokens = totalTasksInProject > 0 ? Math.round((milestone.tasks.length / totalTasksInProject) * projectTokens) : 0
  const tokenPerTask = milestone.tasks.length > 0 ? Math.round(milestoneTokens / milestone.tasks.length) : 0

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden
      hover:border-white/[0.1] transition-all duration-300"
      style={{ animation: `fadeInUp 0.5s ease-out ${index * 80}ms both` }}>

      {/* Header — clickable */}
      <button onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 text-right hover:bg-white/[0.02] transition-all">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
          style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
          {milestone.icon}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-[14px] font-bold text-white">{milestone.name}</h4>
            <Badge status={stats.inProgress > 0 ? 'active' : stats.pending > 0 ? 'pending' : 'done'} />
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-[10px] text-[#94A3B8]">{stats.done}/{stats.total} مهام</span>
            <span className="text-[10px] text-[#64748B]">📅 {milestone.dueDate}</span>
            <TokenChip tokens={milestoneTokens} />
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center gap-3">
          <div className="text-center">
            <div className="text-lg font-bold" style={{ color }}>{stats.avgProgress}%</div>
            <div className="text-[9px] text-[#475569]">إنجاز</div>
          </div>
          <svg className={`w-4 h-4 text-[#475569] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      <div className="px-4 pb-2">
        <Bar progress={stats.avgProgress} color={color} />
      </div>

      {isOpen && (
        <div className="px-4 pb-4 space-y-2 border-t border-white/[0.04] pt-3">
          <div className="flex items-center gap-4 px-2 pb-2 text-[10px] text-[#64748B]">
            <span className="text-emerald-400">✓ {stats.done} مكتمل</span>
            <span className="text-amber-400">⏳ {stats.inProgress} قيد التنفيذ</span>
            <span className="text-[#94A3B8]">○ {stats.pending} معلق</span>
            {stats.overdue > 0 && <span className="text-red-400">⚠️ {stats.overdue} متأخر</span>}
            <span className="mr-auto"><TokenChip tokens={milestoneTokens} /></span>
          </div>
          {milestone.tasks.map((task, i) => (
            <TaskRow key={task.id} task={task} tokenPerTask={tokenPerTask} index={i} />
          ))}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════
//  PROJECT SUMMARY CARD (with real data)
// ═══════════════════════════════════
function ProjectSummaryCard({ productKey, projectMilestones, realProject, isSelected, onClick }) {
  const config = products[productKey] || {}

  // Use REAL progress from API instead of hardcoded milestone data
  const realProgress = realProject?.progress ?? 0
  const realStatus = realProject?.status || 'unknown'
  const completedItems = realProject?.completedItems ?? 0
  const totalItems = realProject?.totalItems ?? 0
  const progressDetails = realProject?.progressDetails || []

  // Real source data
  const sourceLines = realProject?.source?.lines || 0
  const sourceFiles = realProject?.source?.files || 0
  const isOnline = realProject?.api?.online || false
  const estimatedTokens = sourceLines * 2

  return (
    <button onClick={onClick}
      className={`text-right p-4 rounded-2xl border transition-all duration-300
        ${isSelected
          ? 'border-white/[0.15] bg-white/[0.04] shadow-lg'
          : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.1] hover:bg-white/[0.03]'
        }`}
      style={isSelected ? { boxShadow: `0 0 30px -10px ${config.primary}20` } : {}}>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="text-2xl">{config.icon}</div>
          <div>
            <h3 className="text-[15px] font-bold text-white">{config.label}</h3>
            <p className="text-[10px] text-[#64748B]">{completedItems}/{totalItems} معايير محققة</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />
          <Badge status={realStatus} />
        </div>
      </div>

      <div className="mb-2">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-[#94A3B8]">{completedItems}/{totalItems} مكتمل</span>
          <span className="font-bold" style={{ color: config.primary }}>{realProgress}%</span>
        </div>
        <Bar progress={realProgress} color={config.primary} />
      </div>

      {/* Real metrics */}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="text-[10px] text-[#64748B] bg-white/[0.03] rounded-lg px-2 py-1">
          📄 <AnimatedNum value={sourceLines} /> سطر
        </span>
        <span className="text-[10px] text-[#64748B] bg-white/[0.03] rounded-lg px-2 py-1">
          📁 <AnimatedNum value={sourceFiles} /> ملف
        </span>
        <TokenChip tokens={estimatedTokens} />
      </div>

      {/* Component breakdown */}
      {realProject?.source?.byComponent && Object.keys(realProject.source.byComponent).length > 1 && (
        <div className="mt-2 space-y-1">
          {Object.entries(realProject.source.byComponent).map(([comp, data]) => (
            <div key={comp} className="flex items-center justify-between text-[9px] text-[#64748B]">
              <span>{comp}</span>
              <span>{data.files} ملف • {data.lines.toLocaleString('ar-SA')} سطر</span>
            </div>
          ))}
        </div>
      )}

      {/* Progress details */}
      {progressDetails.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {progressDetails.map((item, i) => (
            <div key={i} className="flex items-center gap-1 text-[9px]">
              <span className={item.done ? 'text-emerald-400' : 'text-red-400'}>
                {item.done ? '✅' : '❌'}
              </span>
              <span className="text-[#94A3B8]">{item.label}</span>
              {item.detail && <span className="text-[#475569] mr-auto">({item.detail})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Muwafaq-specific: compliance data */}
      {productKey === 'muwafaq' && realProject?.data && (
        <div className="mt-2 bg-white/[0.02] rounded-lg p-2">
          <div className="text-[10px] text-[#94A3B8] mb-1">📊 الامتثال NCA</div>
          <div className="flex items-center gap-3 text-[11px]">
            <span className="text-emerald-400">{realProject.data.verified_controls || 0}/{realProject.data.total_controls || 181}</span>
            <span className="text-[#64748B">ضابط محقق</span>
            <span className="mr-auto font-bold" style={{ color: config.primary }}>
              {realProject.data.compliance_score || 0}%
            </span>
          </div>
        </div>
      )}
    </button>
  )
}

// ═══════════════════════════════════
//  MAIN PROJECTS SECTION
// ═══════════════════════════════════
export default function ProjectsSection() {
  const [selectedProject, setSelectedProject] = useState('rafiq')
  const [openMilestones, setOpenMilestones] = useState({})
  const [realData, setRealData] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadData = useCallback(async () => {
    const data = await fetchRealProjects()
    setRealData(data)
    setLoading(false)
  }, [])

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 60000) // Refresh every minute
    return () => clearInterval(interval)
  }, [loadData])

  // Group milestones by product
  const projectGroups = useMemo(() => {
    const groups = {}
    milestones.forEach(m => {
      if (!groups[m.product]) groups[m.product] = []
      groups[m.product].push(m)
    })
    return groups
  }, [])

  const projectKeys = ['rafiq', 'muwafaq', 'sahha', 'labor', 'dashboard', 'infra', 'ncaEcc', 'aiRacingGame']
  const selectedMilestones = projectGroups[selectedProject] || []
  const allSelectedTasks = selectedMilestones.flatMap(m => m.tasks)
  const totalDone = allSelectedTasks.filter(t => t.status === 'done').length

  // Real data for selected project
  const realProject = realData?.projects?.[selectedProject]
  const sourceLines = realProject?.source?.lines || 0
  const estimatedTokens = sourceLines * 2

  const toggleMilestone = (id) => {
    setOpenMilestones(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // Total tokens across all projects
  const totalTokens = useMemo(() => {
    if (!realData?.projects) return 0
    return Object.values(realData.projects).reduce((sum, p) => sum + (p.source?.lines || 0) * 2, 0)
  }, [realData])

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-white flex items-center gap-2">
          <span className="text-lg">📐</span> إدارة المشاريع
          {loading && <span className="text-[10px] text-[#475569] animate-pulse">جاري التحميل...</span>}
        </h2>
        <div className="flex items-center gap-3">
          <button onClick={loadData}
            className="text-[10px] bg-white/[0.04] hover:bg-white/[0.08] rounded-lg px-2.5 py-1.5
              text-[#94A3B8] hover:text-white transition-all">
            🔄 تحديث
          </button>
          <div className="flex items-center gap-2 text-[10px] text-[#64748B] bg-white/[0.03] rounded-lg px-3 py-1.5">
            🪙 إجمالي: <span className="text-amber-400 font-bold">{totalTokens.toLocaleString('ar-SA')}</span> Token
          </div>
        </div>
      </div>

      {/* Project Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {projectKeys.map((key) => (
          <ProjectSummaryCard
            key={key}
            productKey={key}
            projectMilestones={projectGroups[key] || []}
            realProject={realData?.projects?.[key]}
            isSelected={selectedProject === key}
            onClick={() => setSelectedProject(key)}
          />
        ))}
      </div>

      {/* Selected Project Detail */}
      <div className="space-y-4">
        {/* Real Progress Stats bar */}
        <div className="flex items-center gap-4 flex-wrap p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center gap-2">
            <span className="text-xl">{products[selectedProject]?.icon}</span>
            <span className="text-sm font-bold text-white">{products[selectedProject]?.label}</span>
          </div>
          <div className="h-4 w-px bg-white/[0.06]" />
          <div className="flex items-center gap-4 text-[11px] flex-wrap">
            <span className="text-emerald-400 font-bold">
              ✓ {realProject?.completedItems ?? 0} مكتمل
            </span>
            <span className="text-red-400">
              ✗ {(realProject?.totalItems ?? 0) - (realProject?.completedItems ?? 0)} ناقص
            </span>
            <span className="text-lg font-bold" style={{ color: products[selectedProject]?.primary }}>
              {realProject?.progress ?? 0}%
            </span>
          </div>
          <div className="h-4 w-px bg-white/[0.06]" />
          <div className="flex items-center gap-3 text-[11px]">
            <span className="text-[#94A3B8] bg-white/[0.03] rounded-lg px-2 py-1">
              📄 <AnimatedNum value={sourceLines} /> سطر كود
            </span>
            <span className="text-[#94A3B8] bg-white/[0.03] rounded-lg px-2 py-1">
              📁 <AnimatedNum value={realProject?.source?.files || 0} /> ملف
            </span>
            <TokenChip tokens={estimatedTokens} />
            {realProject?.api?.online && (
              <span className="text-emerald-400 text-[10px]">● مباشر</span>
            )}
          </div>
        </div>

        {/* Real Progress Details — Checklist */}
        {realProject?.progressDetails?.length > 0 && (
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-[12px] font-bold text-white mb-3">📋 تفاصيل التقدم الفعلي</h4>
            <div className="space-y-2">
              {realProject.progressDetails.map((item, i) => {
                const color = products[selectedProject]?.primary || '#8B5CF6'
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all"
                    style={{ animation: `fadeInUp 0.4s ease-out ${i * 60}ms both` }}>
                    {/* Status icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm
                      ${item.done ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
                      {item.done ? '✅' : '❌'}
                    </div>
                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium text-white">{item.label}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full
                          ${item.done ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                          {item.done ? 'جاهز' : 'ناقص'}
                        </span>
                      </div>
                      {item.detail && (
                        <span className="text-[10px] text-[#64748B]">{item.detail}</span>
                      )}
                    </div>
                    {/* Weight badge */}
                    <div className="text-[10px] text-[#475569] bg-white/[0.04] rounded-lg px-2 py-1 flex-shrink-0">
                      وزن {item.weight}%
                    </div>
                    {/* Progress contribution */}
                    <div className="w-16 flex-shrink-0">
                      <Bar progress={item.done ? 100 : 0} color={item.done ? '#22C55E' : '#EF4444'} height="h-1.5" />
                    </div>
                  </div>
                )
              })}
            </div>
            {/* Summary bar */}
            <div className="mt-3 pt-3 border-t border-white/[0.04]">
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span className="text-[#94A3B8]">التقدم الكلي</span>
                <span className="font-bold" style={{ color: products[selectedProject]?.primary }}>
                  {realProject?.progress ?? 0}%
                </span>
              </div>
              <Bar progress={realProject?.progress ?? 0} color={products[selectedProject]?.primary || '#8B5CF6'} height="h-3" />
            </div>
          </div>
        )}

        {/* Component breakdown */}
        {realProject?.source?.byComponent && Object.keys(realProject.source.byComponent).length > 1 && (
          <div className="flex items-center gap-3 flex-wrap px-4">
            {Object.entries(realProject.source.byComponent).map(([comp, data]) => (
              <div key={comp} className="bg-white/[0.03] rounded-lg px-3 py-2 text-[11px]">
                <span className="text-[#94A3B8]">{comp}:</span>{' '}
                <span className="text-white font-medium">{data.files} ملف</span>{' '}
                <span className="text-[#64748B]">({data.lines.toLocaleString('ar-SA')} سطر)</span>
              </div>
            ))}
          </div>
        )}

        {/* Muwafaq live compliance */}
        {selectedProject === 'muwafaq' && realProject?.dashboardData && (
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-[12px] font-bold text-white mb-3">📊 نسبة الامتثال الفعلية — NCA ECC</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(realProject.dashboardData.domainBreakdown || {}).map(([domain, data]) => (
                <div key={domain} className="bg-white/[0.03] rounded-xl p-3">
                  <div className="text-[10px] text-[#64748B] truncate">{domain}</div>
                  <div className="text-lg font-bold mt-1" style={{ color: '#4CAF50' }}>
                    {data.total > 0 ? Math.round((data.verified / data.total) * 100) : 0}%
                  </div>
                  <div className="text-[9px] text-[#475569]">
                    {data.verified}/{data.total} ضابط
                  </div>
                  <div className="mt-1">
                    <Bar progress={data.total > 0 ? (data.verified / data.total) * 100 : 0} color="#4CAF50" height="h-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Milestones list (from hardcoded data — for reference) */}
        {selectedMilestones.length > 0 && selectedProject !== 'ncaEcc' && (
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-[12px] font-bold text-white mb-3">📅 مراحل المشروع (من خطة المهام)</h4>
            <div className="space-y-2">
              {selectedMilestones.map((ms, i) => {
                const stats = getMilestoneStats(ms)
                const color = products[selectedProject]?.primary || '#8B5CF6'
                return (
                  <button key={ms.id}
                    onClick={() => toggleMilestone(ms.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all text-right">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0"
                      style={{ background: `${color}15` }}>
                      {ms.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-[13px] font-medium text-white">{ms.name}</div>
                      <div className="text-[10px] text-[#64748B]">
                        {stats.done}/{stats.total} مهام • 📅 {ms.dueDate}
                      </div>
                    </div>
                    <div className="text-[11px] font-bold" style={{ color }}>{stats.avgProgress}%</div>
                    <svg className={`w-4 h-4 text-[#475569] transition-transform ${openMilestones[ms.id] ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )
              })}
            </div>
          </div>
        )}
        
        {/* NCA ECC detailed plan — only for ncaEcc project */}
        {selectedProject === 'ncaEcc' && (
          <NCAEccPlan realProject={realProject} />
        )}
      </div>
    </div>
  )
}
